/**
 * Request builders that only need the NanomeModel (no SESAM, no Molstar), so the main thread can import them from
 * '@nanome-lite/surface/client' without pulling the compute pipeline into its bundle.
 */
import type { NanomeModel } from '@nanome-lite/data';
import { VDW_RADII, VDW_RADIUS_DEFAULT } from '@nanome-lite/look';
import type { SurfaceRequest } from './types';

/** vdW radius per atom from the Nanome table (AtomicRadius.cs via @nanome-lite/look VDW_RADII; unknown element 2.0). */
export function radiiFromModel(model: NanomeModel): Float32Array {
  const out = new Float32Array(model.atomCount);
  for (let i = 0; i < model.atomCount; i++) {
    const r = VDW_RADII[model.element[i]!];   // index 0 (unknown element) holds 0
    out[i] = r && r > 0 ? r : VDW_RADIUS_DEFAULT;
  }
  return out;
}

/**
 * The atoms a subsurface is computed over: every polymer atom (Protein, Nucleic, NonStandardProtein,
 * NonStandardNucleic) of each chain that holds at least one of `atoms` (RenderingViewModel.Surface.cs:53-91).
 */
export function wholeChainAtoms(model: NanomeModel, atoms: Uint32Array): Uint32Array {
  const chains = new Set<number>();
  for (let i = 0; i < atoms.length; i++) chains.add(model.chainIndex[atoms[i]!]!);
  const out: number[] = [];
  for (let a = 0; a < model.atomCount; a++) {
    if (!chains.has(model.chainIndex[a]!)) continue;
    const s = model.substructure[a]!;
    // Substructure enum (packages/data/src/model.ts): Protein 1, Nucleic 2, NonStandardProtein 7, NonStandardNucleic 8
    if (s === 1 || s === 2 || s === 7 || s === 8) out.push(a);
  }
  return Uint32Array.from(out);
}

/** Builds a subsurface request for `pocketAtoms` (model indices) using the model's current frame. */
export function subsurfaceRequest(model: NanomeModel, pocketAtoms: Uint32Array, radius: Float32Array = radiiFromModel(model), options: Partial<Omit<SurfaceRequest, 'xyz' | 'radius' | 'chainIndex' | 'atomIndices' | 'keepAtoms'>> = {}): SurfaceRequest {
  return { ...options, xyz: model.xyz, radius, chainIndex: model.chainIndex, atomIndices: wholeChainAtoms(model, pocketAtoms), keepAtoms: pocketAtoms };
}
